// Yorick's passive: every second he raises a Ghoul at a nearby unoccupied hex.
// Ghouls are fast, frail (1 HP), melee auto-attackers that die in a single hit.

import type { Ability, AbilityHit } from "./Ability";
import { hexesWithinRange, hexDistance } from "../../engine/hex/HexCoord";
import type { HexCoord } from "../../engine/hex/HexCoord";
import type { CombatContext } from "../../combat/CombatContext";
import { buildYorickGhoulUnit } from "../../combat/UnitFactory";

function findSpawnHex(near: HexCoord, combat: CombatContext): HexCoord | null {
  // Range 5 covers the full 5-slot × 2-row grid (max corner-to-corner distance = 5).
  const candidates = hexesWithinRange(near, 5)
    .filter(h => hexDistance(near, h) >= 1 && combat.inBounds(h) && !combat.isBlocked(h));
  candidates.sort((a, b) => hexDistance(near, a) - hexDistance(near, b));
  return candidates[0] ?? null;
}

export const yorickSummonGhoul: Ability = {
  id: "yorickSummonGhoul",
  displayName: "Raise Ghoul",
  description:
    "Yorick raises a Ghoul every second at a nearby hex. Ghouls rush enemies and die in one hit.",
  iconId: "Yorick",

  targetShape({ caster, combat }): HexCoord[] {
    const spawnHex = findSpawnHex(caster.pos, combat);
    return spawnHex ? [spawnHex] : [caster.pos];
  },

  computeHits({ caster, combat }): AbilityHit[] {
    const spawnHex = findSpawnHex(caster.pos, combat);
    if (!spawnHex) return [];
    const ghoul = buildYorickGhoulUnit(spawnHex, caster.team, caster.level);
    combat.spawnUnit(ghoul);
    return [];
  },
};
